import { defineStore } from 'pinia'
import { ref } from 'vue'

import { commercialApi } from '@/interfaces/spark-api'
import { components } from '#/types/capi-generated'

type IntradayUnit = components['schemas']['IntradayUnit']
type IntradayContract = components['schemas']['IntradayContract']
type IntradayContractUnit =
  components['schemas']['commercial_api__views__intraday__v1_0__contracts__common__IntradayContractUnit']
type BreakevenType = components['schemas']['CAPIIntradayBreakevenType']
type PointEnum = components['schemas']['PointEnum']
type PercentHire = components['schemas']['IntradayPercentageEnum']

export type IntradayAdvancedOpts = {
  percentHire?: PercentHire
  extendedCurve?: boolean
}

export type IntradayFobPort = {
  uuid: string
  name: string
  viaPoint: PointEnum | null
}

type TableResult = { columns: string[]; rows: (string | number)[][] }

function extractApiError(err: unknown): Error {
  if (typeof err === 'object' && err !== null) {
    const anyErr = err as { response?: { data?: { errors?: { detail?: string }[] } }; message?: string }
    const details = anyErr.response?.data?.errors?.map((e) => e.detail).filter(Boolean) as string[] | undefined
    if (details && details.length > 0) return new Error(details.join('; '))
    if (anyErr.message) return new Error(anyErr.message)
  }
  return new Error('Request failed')
}

function toPascalCase(key: string): string {
  return key.length === 0 ? key : key[0].toUpperCase() + key.slice(1)
}

function flattenDataPoints(
  data: Record<string, unknown>[],
  leading: { column: string; value: string }[] = [],
): TableResult {
  const leadingColumns = leading.map((l) => l.column)
  const leadingByColumn = new Map(leading.map((l) => [l.column, l.value]))

  if (data.length === 0) {
    return { columns: leadingColumns.map(toPascalCase), rows: [] }
  }

  const dataColumns: string[] = []
  const seen = new Set<string>(leadingColumns)
  for (const dp of data) {
    for (const key of Object.keys(dp)) {
      if (!seen.has(key)) {
        seen.add(key)
        dataColumns.push(key)
      }
    }
  }

  const PINNED_FIRST = 'asOf'
  const hasPinned = dataColumns.includes(PINNED_FIRST)
  const columns: string[] = [
    ...(hasPinned ? [PINNED_FIRST] : []),
    ...leadingColumns,
    ...dataColumns.filter((c) => c !== PINNED_FIRST),
  ]

  const rows = data.map((dp) =>
    columns.map((col) => {
      if (leadingByColumn.has(col)) return leadingByColumn.get(col)!
      const v = dp[col]
      if (v === null || v === undefined) return ''
      if (col === 'periodName' && (typeof v === 'string' || typeof v === 'number')) {
        return `'${v}`
      }
      if (typeof v === 'number' || typeof v === 'string') return v
      return String(v)
    }),
  )

  return { columns: columns.map(toPascalCase), rows }
}

export const useIntradayStore = defineStore('intraday', () => {
  const hasIntradayPermission = ref(false)
  const arbsFobPorts = ref<IntradayFobPort[]>([])
  const breakevensFobPorts = ref<IntradayFobPort[]>([])

  function findFobPort(
    kind: 'arbs' | 'breakevens',
    uuid: string,
    viaPoint: PointEnum | null,
  ): IntradayFobPort | undefined {
    const list = kind === 'arbs' ? arbsFobPorts.value : breakevensFobPorts.value
    return list.find((p) => p.uuid === uuid && p.viaPoint === viaPoint)
  }

  function fobPortName(
    kind: 'arbs' | 'breakevens',
    uuid: string,
    viaPoint: PointEnum | null,
  ): string {
    return findFobPort(kind, uuid, viaPoint)?.name ?? uuid
  }

  async function fetchFobPortReferenceData(
    kind: 'arbs' | 'breakevens',
  ): Promise<IntradayFobPort[]> {
    const cache = kind === 'arbs' ? arbsFobPorts : breakevensFobPorts
    if (cache.value.length > 0) return cache.value

    try {
      const res =
        kind === 'arbs'
          ? await commercialApi.fetchIntradayArbsReferenceData()
          : await commercialApi.fetchIntradayBreakevensReferenceData()
      const ports: IntradayFobPort[] = (res.data ?? []).map((p) => ({
        uuid: p.fobPortUuid,
        name: p.fobPortName,
        viaPoint: (p.viaPoint ?? null) as PointEnum | null,
      }))
      cache.value = ports
      hasIntradayPermission.value = true
      return ports
    } catch (err) {
      console.error(err)
      return []
    }
  }

  async function fetchContractsLive(
    contract: IntradayContract,
    unit: IntradayContractUnit,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayContractsLive(contract, unit)
      const table = flattenDataPoints(res.data as unknown as Record<string, unknown>[])
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  async function fetchContractsHistorical(
    contract: IntradayContract,
    unit: IntradayContractUnit,
    start: string,
    end: string,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayContractsHistorical(
        contract,
        unit,
        start,
        end,
      )
      const table = flattenDataPoints(res.data as unknown as Record<string, unknown>[])
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  async function fetchArbsLive(
    unit: IntradayUnit,
    fobPortUuid: string,
    viaPoint: PointEnum | null,
    opts?: IntradayAdvancedOpts,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayArbsLive(
        unit,
        fobPortUuid,
        viaPoint,
        opts,
      )
      const table = flattenDataPoints(
        res.data as unknown as Record<string, unknown>[],
        [
          { column: 'fobPort', value: fobPortName('arbs', fobPortUuid, viaPoint) },
          { column: 'viaPoint', value: viaPoint ?? '' },
          { column: 'unit', value: unit },
          { column: 'percentHire', value: opts?.percentHire ?? '100' },
        ],
      )
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  async function fetchArbsHistorical(
    unit: IntradayUnit,
    fobPortUuid: string,
    start: string,
    end: string,
    viaPoint: PointEnum | null,
    opts?: IntradayAdvancedOpts,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayArbsHistorical(
        unit,
        fobPortUuid,
        start,
        end,
        viaPoint,
        opts,
      )
      const table = flattenDataPoints(
        res.data as unknown as Record<string, unknown>[],
        [
          { column: 'fobPort', value: fobPortName('arbs', fobPortUuid, viaPoint) },
          { column: 'viaPoint', value: viaPoint ?? '' },
          { column: 'unit', value: unit },
          { column: 'percentHire', value: opts?.percentHire ?? '100' },
        ],
      )
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  async function fetchBreakevensLive(
    unit: IntradayUnit,
    breakevenType: BreakevenType,
    fobPortUuid: string,
    viaPoint: PointEnum | null,
    opts?: IntradayAdvancedOpts,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayBreakevensLive(
        unit,
        breakevenType,
        fobPortUuid,
        viaPoint,
        opts,
      )
      const table = flattenDataPoints(
        res.data as unknown as Record<string, unknown>[],
        [
          { column: 'fobPort', value: fobPortName('breakevens', fobPortUuid, viaPoint) },
          { column: 'viaPoint', value: viaPoint ?? '' },
          { column: 'unit', value: unit },
          { column: 'percentHire', value: opts?.percentHire ?? '100' },
        ],
      )
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  async function fetchBreakevensHistorical(
    unit: IntradayUnit,
    breakevenType: BreakevenType,
    fobPortUuid: string,
    start: string,
    end: string,
    viaPoint: PointEnum | null,
    opts?: IntradayAdvancedOpts,
  ): Promise<TableResult> {
    try {
      const res = await commercialApi.fetchIntradayBreakevensHistorical(
        unit,
        breakevenType,
        fobPortUuid,
        start,
        end,
        viaPoint,
        opts,
      )
      const table = flattenDataPoints(
        res.data as unknown as Record<string, unknown>[],
        [
          { column: 'fobPort', value: fobPortName('breakevens', fobPortUuid, viaPoint) },
          { column: 'viaPoint', value: viaPoint ?? '' },
          { column: 'unit', value: unit },
          { column: 'percentHire', value: opts?.percentHire ?? '100' },
        ],
      )
      hasIntradayPermission.value = true
      return table
    } catch (err) {
      console.error(err)
      throw extractApiError(err)
    }
  }

  return {
    hasIntradayPermission,
    arbsFobPorts,
    breakevensFobPorts,
    fetchFobPortReferenceData,
    fetchContractsLive,
    fetchContractsHistorical,
    fetchArbsLive,
    fetchArbsHistorical,
    fetchBreakevensLive,
    fetchBreakevensHistorical,
  }
})
